import { useRef, useState, type ChangeEvent, type DragEvent, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getTokens } from '../api';

interface Track {
  _id: string;
  title: string;
  artistId: string;
  durationSec: number;
  plays: number;
  genre: string;
  transcodeStatus?: 'processing' | 'ready' | 'failed';
  transcodeError?: string;
}

interface Artist {
  _id: string;
  stageName: string;
}

const GENRES = [
  'rumba',
  'coupe-decale',
  'afrobeat',
  'soukous',
  'ndombolo',
  'gospel',
  'hip-hop',
  'rnb',
  'zouk',
  'other',
] as const;

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4000';

export default function Tracks() {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { data: artists } = useQuery<{ items: Artist[] }>({
    queryKey: ['artists', 'all'],
    queryFn: () => api('/api/v1/artists?limit=200'),
  });

  // Auto-refetch every 5s while any track is still processing.
  const { data } = useQuery<{ items: Track[] }>({
    queryKey: ['admin', 'tracks'],
    queryFn: () => api('/api/v1/tracks?limit=100'),
    refetchInterval: (query) => {
      const items = (query.state.data as { items?: Track[] } | undefined)?.items ?? [];
      return items.some((t) => t.transcodeStatus === 'processing') ? 5_000 : false;
    },
  });

  const [form, setForm] = useState({
    artistId: '',
    albumId: '',
    title: '',
    durationSec: 180,
    genre: 'rumba' as (typeof GENRES)[number],
    releaseDate: new Date().toISOString().slice(0, 10),
    order: 0,
  });

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  const handleFile = (f: File) => {
    setUploadError(null);
    setPendingFile(f);
    if (!form.title) {
      const guess = f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ');
      setForm((s) => ({ ...s, title: guess }));
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!pendingFile) {
      setUploadError('Pick or drop an audio file first');
      return;
    }
    if (!form.artistId) {
      setUploadError('Select an artist');
      return;
    }

    setUploadError(null);
    setUploadProgress(0);

    const fd = new FormData();
    fd.append('audio', pendingFile);
    fd.append('artistId', form.artistId);
    if (form.albumId) fd.append('albumId', form.albumId);
    fd.append('title', form.title);
    fd.append('durationSec', String(form.durationSec));
    fd.append('genre', form.genre);
    fd.append('releaseDate', form.releaseDate);
    fd.append('order', String(form.order));

    try {
      const tokens = getTokens();
      const out = await uploadWithProgress(
        `${API_URL}/api/v1/admin/tracks/upload`,
        fd,
        tokens?.accessToken,
        (r) => setUploadProgress(r),
      );
      if (out.status >= 400) {
        const err = (() => {
          try {
            return JSON.parse(out.responseText) as { error?: { message?: string } };
          } catch {
            return null;
          }
        })();
        throw new Error(err?.error?.message ?? `Upload failed (${out.status})`);
      }
      setPendingFile(null);
      setUploadProgress(null);
      setForm((s) => ({ ...s, title: '' }));
      if (inputRef.current) inputRef.current.value = '';
      void qc.invalidateQueries({ queryKey: ['admin', 'tracks'] });
    } catch (err) {
      setUploadError((err as Error).message);
      setUploadProgress(null);
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Tracks</h1>

      <form onSubmit={submit} className="bg-surface border border-border rounded-xl p-6 mb-8">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`mb-4 cursor-pointer rounded-lg border-2 border-dashed transition flex flex-col items-center justify-center text-center px-6 py-10 ${
            dragOver ? 'border-accent bg-surface2' : 'border-border bg-bg'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="audio/*,.mp3,.m4a,.wav,.flac,.aac,.ogg"
            className="hidden"
            onChange={onPick}
          />
          {pendingFile ? (
            <>
              <div className="text-accent font-semibold">{pendingFile.name}</div>
              <div className="text-text2 text-xs mt-1">{(pendingFile.size / 1_000_000).toFixed(1)} MB</div>
            </>
          ) : (
            <>
              <div className="text-text2 text-sm">Drag &amp; drop audio file or click to pick</div>
              <div className="text-textmuted text-xs mt-1">mp3 · m4a · wav · flac · aac · ogg (≤ 200 MB)</div>
            </>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <select
            value={form.artistId}
            onChange={(e) => setForm({ ...form, artistId: e.target.value })}
            className="bg-bg border border-border rounded-md px-3 py-2"
          >
            <option value="">Select artist</option>
            {artists?.items?.map((a) => (
              <option key={a._id} value={a._id}>
                {a.stageName}
              </option>
            ))}
          </select>
          <input
            placeholder="Album ID (optional)"
            value={form.albumId}
            onChange={(e) => setForm({ ...form, albumId: e.target.value })}
            className="bg-bg border border-border rounded-md px-3 py-2 font-mono text-xs"
          />
          <input
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="bg-bg border border-border rounded-md px-3 py-2 col-span-2"
          />
          <input
            type="number"
            min={1}
            placeholder="Duration (sec)"
            value={form.durationSec}
            onChange={(e) => setForm({ ...form, durationSec: Number(e.target.value) })}
            className="bg-bg border border-border rounded-md px-3 py-2"
          />
          <select
            value={form.genre}
            onChange={(e) => setForm({ ...form, genre: e.target.value as (typeof GENRES)[number] })}
            className="bg-bg border border-border rounded-md px-3 py-2"
          >
            {GENRES.map((g) => (
              <option key={g}>{g}</option>
            ))}
          </select>
          <input
            type="date"
            value={form.releaseDate}
            onChange={(e) => setForm({ ...form, releaseDate: e.target.value })}
            className="bg-bg border border-border rounded-md px-3 py-2"
          />
          <input
            type="number"
            placeholder="Track # (order)"
            value={form.order}
            onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
            className="bg-bg border border-border rounded-md px-3 py-2"
          />
        </div>

        {uploadError ? <div className="mt-3 text-danger text-sm">{uploadError}</div> : null}

        <button
          type="submit"
          disabled={uploadProgress !== null || !pendingFile}
          className="mt-4 w-full bg-accent text-bg px-4 py-2 rounded-md font-semibold disabled:opacity-50"
        >
          {uploadProgress !== null
            ? `Uploading… ${Math.round(uploadProgress * 100)}%`
            : 'Upload + transcode'}
        </button>
        {uploadProgress !== null ? (
          <div className="h-1 bg-border rounded-full mt-2 overflow-hidden">
            <div
              className="h-full bg-accent transition-all"
              style={{ width: `${Math.round(uploadProgress * 100)}%` }}
            />
          </div>
        ) : null}
      </form>

      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface2 text-left text-text2">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Genre</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Duration</th>
              <th className="px-4 py-3 text-right">Plays</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((t) => (
              <tr key={t._id} className="border-t border-border">
                <td className="px-4 py-3">{t.title}</td>
                <td className="px-4 py-3 text-accent">{t.genre}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={t.transcodeStatus ?? 'ready'} error={t.transcodeError} />
                </td>
                <td className="px-4 py-3 text-right">
                  {Math.floor(t.durationSec / 60)}:{String(t.durationSec % 60).padStart(2, '0')}
                </td>
                <td className="px-4 py-3 text-right">{t.plays.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({
  status,
  error,
}: {
  status: 'processing' | 'ready' | 'failed';
  error?: string;
}) {
  const cls =
    status === 'ready'
      ? 'bg-success text-bg'
      : status === 'processing'
        ? 'bg-accentDim text-bg'
        : 'bg-danger text-bg';
  return (
    <span title={error} className={`px-2 py-0.5 rounded-full text-xs ${cls}`}>
      {status}
    </span>
  );
}

/**
 * Native fetch can't report upload progress. XMLHttpRequest can, which is the
 * one place in the admin we still reach for it.
 */
function uploadWithProgress(
  url: string,
  body: FormData,
  token: string | undefined,
  onProgress: (ratio: number) => void,
): Promise<{ status: number; responseText: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => resolve({ status: xhr.status, responseText: xhr.responseText });
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(body);
  });
}
